import Layout from '../../components/base/Layout';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

export default function ElektrischSchakelen() {
  const { t } = useTranslation();
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, string>>({});
  const [showResults, setShowResults] = useState(false);

  const correctAnswers: Record<number, string> = {
    1: 'B',
    2: 'C',
    3: 'A'
  };

  const handleAnswerSelect = (questionId: number, answer: string) => {
    setSelectedAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const checkAnswers = () => {
    setShowResults(true);
  };

  const resetQuiz = () => {
    setSelectedAnswers({});
    setShowResults(false);
  };

  const getScore = () => {
    let correct = 0;
    Object.keys(correctAnswers).forEach(key => {
      if (selectedAnswers[parseInt(key)] === correctAnswers[parseInt(key)]) {
        correct++;
      }
    });
    return correct;
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 flex items-center justify-center bg-blue-600 rounded-2xl mx-auto mb-6">
            <i className="ri-flashlight-line text-4xl text-white"></i>
          </div>
          <h1 className="text-5xl font-bold text-gray-800 mb-4">
            {t('elec_title')}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('elec_subtitle')}
          </p>
        </div>

        {/* 1. Wat is elektrisch schakelen? */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 shadow-lg border border-blue-200">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-14 h-14 flex items-center justify-center bg-blue-600 rounded-xl">
              <i className="ri-lightbulb-line text-3xl text-white"></i>
            </div>
            <h2 className="text-3xl font-bold text-blue-900">{t('elec_what_title')}</h2>
          </div>
          
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            {t('elec_what_desc')}
          </p>

          <div className="bg-white rounded-xl p-6 mb-6">
            <h3 className="text-xl font-semibold mb-4 text-blue-800">{t('elec_examples')}</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { icon: 'ri-lightbulb-flash-line', text: t('elec_ex1') },
                { icon: 'ri-cpu-line', text: t('elec_ex2') },
                { icon: 'ri-plug-line', text: t('elec_ex3') },
                { icon: 'ri-settings-3-line', text: t('elec_ex4') },
                { icon: 'ri-stop-circle-line', text: t('elec_ex5') },
                { icon: 'ri-play-circle-line', text: t('elec_ex6') }
              ].map((item, index) => (
                <div key={index} className="flex items-center space-x-3 bg-blue-50 p-4 rounded-lg">
                  <div className="w-10 h-10 flex items-center justify-center bg-blue-100 rounded-lg">
                    <i className={`${item.icon} text-xl text-blue-600`}></i>
                  </div>
                  <span className="text-gray-700 font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-lg">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-lg">
                  <i className="ri-checkbox-circle-line text-2xl text-green-600"></i>
                </div>
                <h4 className="text-lg font-semibold text-green-800">{t('elec_closed')}</h4>
              </div>
              <p className="text-gray-700">{t('elec_closed_desc')}</p>
            </div>

            <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-lg">
                  <i className="ri-close-circle-line text-2xl text-red-600"></i>
                </div>
                <h4 className="text-lg font-semibold text-red-800">{t('elec_open')}</h4>
              </div>
              <p className="text-gray-700">{t('elec_open_desc')}</p>
            </div>
          </div>
        </div>

        {/* 2. Basisbegrippen */}
        <div className="bg-white rounded-2xl p-8 shadow-lg">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-14 h-14 flex items-center justify-center bg-indigo-600 rounded-xl">
              <i className="ri-book-open-line text-3xl text-white"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-800">{t('elec_basics_title')}</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-6 rounded-xl border border-indigo-200">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 flex items-center justify-center bg-indigo-600 rounded-lg">
                  <i className="ri-arrow-right-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-semibold text-indigo-900">{t('elec_series')}</h3>
              </div>
              <p className="text-gray-700 mb-3">{t('elec_series_desc')}</p>
              <div className="bg-white p-4 rounded-lg">
                <code className="text-sm text-indigo-700">→ [Schakelaar 1] → [Schakelaar 2] → [Lamp] →</code>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl border border-purple-200">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 flex items-center justify-center bg-purple-600 rounded-lg">
                  <i className="ri-git-branch-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-semibold text-purple-900">{t('elec_parallel')}</h3>
              </div>
              <p className="text-gray-700 mb-3">{t('elec_parallel_desc')}</p>
              <div className="bg-white p-4 rounded-lg">
                <code className="text-sm text-purple-700">→ [Schakelaar 1] ↓<br/>→ [Schakelaar 2] → [Lamp]</code>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-green-50 p-6 rounded-xl border-l-4 border-green-500">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-lg">
                  <span className="text-2xl font-bold text-green-600">NO</span>
                </div>
                <h3 className="text-xl font-semibold text-green-800">{t('elec_no')}</h3>
              </div>
              <p className="text-gray-700">{t('elec_no_desc')}</p>
            </div>

            <div className="bg-orange-50 p-6 rounded-xl border-l-4 border-orange-500">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-lg">
                  <span className="text-2xl font-bold text-orange-600">NC</span>
                </div>
                <h3 className="text-xl font-semibold text-orange-800">{t('elec_nc')}</h3>
              </div>
              <p className="text-gray-700">{t('elec_nc_desc')}</p>
            </div>
          </div>
        </div>

        {/* 3. Soorten schakelaars */}
        <div className="bg-white rounded-2xl p-8 shadow-lg">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-14 h-14 flex items-center justify-center bg-cyan-600 rounded-xl">
              <i className="ri-toggle-line text-3xl text-white"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-800">{t('elec_types_title')}</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: 'ri-toggle-line', color: 'blue', title: t('elec_type1'), desc: t('elec_type1_desc') },
              { icon: 'ri-toggle-fill', color: 'green', title: t('elec_type2'), desc: t('elec_type2_desc') },
              { icon: 'ri-radio-button-line', color: 'purple', title: t('elec_type3'), desc: t('elec_type3_desc') },
              { icon: 'ri-plug-line', color: 'orange', title: t('elec_type4'), desc: t('elec_type4_desc') },
              { icon: 'ri-cpu-line', color: 'red', title: t('elec_type5'), desc: t('elec_type5_desc') },
              { icon: 'ri-settings-3-line', color: 'indigo', title: t('elec_type6'), desc: t('elec_type6_desc') },
              { icon: 'ri-stop-line', color: 'yellow', title: t('elec_type7'), desc: t('elec_type7_desc') },
              { icon: 'ri-shield-check-line', color: 'teal', title: t('elec_type8'), desc: t('elec_type8_desc') }
            ].map((item, index) => (
              <div key={index} className={`bg-${item.color}-50 p-6 rounded-xl border border-${item.color}-200 hover:shadow-lg transition-shadow`}>
                <div className={`w-14 h-14 flex items-center justify-center bg-${item.color}-100 rounded-xl mb-4`}>
                  <i className={`${item.icon} text-3xl text-${item.color}-600`}></i>
                </div>
                <h3 className={`text-lg font-semibold mb-2 text-${item.color}-900`}>{item.title}</h3>
                <p className="text-gray-700 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-8">
            <img 
              src="https://public.readdy.ai/ai/img_res/2d6f6629257617ac982251938ab17303.jpg"
              alt={t('elec_types_title')}
              className="w-full rounded-xl shadow-md object-cover object-top"
            />
          </div>
        </div>

        {/* 4. Industriële systemen */}
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 shadow-lg">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-14 h-14 flex items-center justify-center bg-gray-700 rounded-xl">
              <i className="ri-factory-line text-3xl text-white"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-800">{t('elec_industrial_title')}</h2>
          </div>

          <p className="text-lg text-gray-700 mb-6">{t('elec_industrial_desc')}</p>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: 'ri-settings-5-line', title: t('elec_ind1'), desc: t('elec_ind1_desc'), color: 'blue' },
              { icon: 'ri-play-circle-line', title: t('elec_ind2'), desc: t('elec_ind2_desc'), color: 'green' },
              { icon: 'ri-sensor-line', title: t('elec_ind3'), desc: t('elec_ind3_desc'), color: 'purple' },
              { icon: 'ri-shield-line', title: t('elec_ind4'), desc: t('elec_ind4_desc'), color: 'red' },
              { icon: 'ri-stop-circle-line', title: t('elec_ind5'), desc: t('elec_ind5_desc'), color: 'orange' },
              { icon: 'ri-error-warning-line', title: t('elec_ind6'), desc: t('elec_ind6_desc'), color: 'yellow' }
            ].map((item, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className={`w-12 h-12 flex items-center justify-center bg-${item.color}-100 rounded-lg flex-shrink-0`}>
                    <i className={`${item.icon} text-2xl text-${item.color}-600`}></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2 text-gray-800">{item.title}</h3>
                    <p className="text-gray-600 text-sm">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 bg-blue-600 text-white p-6 rounded-xl">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 flex items-center justify-center bg-blue-500 rounded-lg flex-shrink-0">
                <i className="ri-information-line text-2xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">{t('elec_beumer_title')}</h3>
                <p className="leading-relaxed">{t('elec_beumer_desc')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* 5. Veilig elektrisch schakelen */}
        <div className="bg-red-50 rounded-2xl p-8 shadow-lg border-2 border-red-300">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-14 h-14 flex items-center justify-center bg-red-600 rounded-xl">
              <i className="ri-shield-check-line text-3xl text-white"></i>
            </div>
            <h2 className="text-3xl font-bold text-red-900">{t('elec_safety_title')}</h2>
          </div>

          <div className="bg-red-100 border-l-4 border-red-600 p-6 rounded-lg mb-6">
            <p className="text-red-900 text-lg font-semibold">
              {t('elec_safety_warning')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: 'ri-shut-down-line', title: t('elec_safe1'), desc: t('elec_safe1_desc') },
              { icon: 'ri-flashlight-line', title: t('elec_safe2'), desc: t('elec_safe2_desc') },
              { icon: 'ri-lock-line', title: t('elec_safe3'), desc: t('elec_safe3_desc') },
              { icon: 'ri-shield-user-line', title: t('elec_safe4'), desc: t('elec_safe4_desc') },
              { icon: 'ri-alert-line', title: t('elec_safe5'), desc: t('elec_safe5_desc') },
              { icon: 'ri-eye-line', title: t('elec_safe6'), desc: t('elec_safe6_desc') }
            ].map((item, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md border-l-4 border-red-500">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-2xl text-red-600`}></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2 text-gray-800">{item.title}</h3>
                    <p className="text-gray-600 text-sm">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6">
            <img 
              src="https://readdy.ai/api/search-image?query=Electrical%20safety%20procedures%20lockout%20tagout%20LOTO%2C%20technician%20wearing%20safety%20equipment%20PPE%20working%20on%20electrical%20panel%2C%20industrial%20safety%20measures%2C%20professional%20electrical%20work%2C%20safety%20first%20approach%20in%20industrial%20environment&width=1200&height=400&seq=safety2&orientation=landscape"
              alt={t('elec_safety_title')}
              className="w-full rounded-xl shadow-md object-cover object-top"
            />
          </div>
        </div>

        {/* 6. Veelvoorkomende fouten */}
        <div className="bg-white rounded-2xl p-8 shadow-lg">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-14 h-14 flex items-center justify-center bg-yellow-600 rounded-xl">
              <i className="ri-tools-line text-3xl text-white"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-800">{t('elec_errors_title')}</h2>
          </div>

          <div className="space-y-4">
            {[
              { 
                error: t('elec_err1'), 
                solution: t('elec_err1_sol'),
                icon: 'ri-plug-line',
                color: 'red'
              },
              { 
                error: t('elec_err2'), 
                solution: t('elec_err2_sol'),
                icon: 'ri-arrow-left-right-line',
                color: 'orange'
              },
              { 
                error: t('elec_err3'), 
                solution: t('elec_err3_sol'),
                icon: 'ri-flashlight-line',
                color: 'yellow'
              },
              { 
                error: t('elec_err4'), 
                solution: t('elec_err4_sol'),
                icon: 'ri-link-unlink-m',
                color: 'purple'
              },
              { 
                error: t('elec_err5'), 
                solution: t('elec_err5_sol'),
                icon: 'ri-sensor-line',
                color: 'blue'
              },
              { 
                error: t('elec_err6'), 
                solution: t('elec_err6_sol'),
                icon: 'ri-settings-3-line',
                color: 'green'
              }
            ].map((item, index) => (
              <div key={index} className={`bg-${item.color}-50 p-6 rounded-xl border-l-4 border-${item.color}-500`}>
                <div className="flex items-start space-x-4">
                  <div className={`w-12 h-12 flex items-center justify-center bg-${item.color}-100 rounded-lg flex-shrink-0`}>
                    <i className={`${item.icon} text-2xl text-${item.color}-600`}></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className={`text-xs font-bold text-${item.color}-700 bg-${item.color}-200 px-3 py-1 rounded-full`}>
                        {t('elec_error')}
                      </span>
                      <h3 className="text-lg font-semibold text-gray-800">{item.error}</h3>
                    </div>
                    <div className="flex items-start space-x-2">
                      <span className="text-xs font-bold text-green-700 bg-green-200 px-3 py-1 rounded-full">
                        {t('elec_solution')}
                      </span>
                      <p className="text-gray-700 flex-1">{item.solution}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 7. Praktische oefeningen */}
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-8 shadow-lg border border-purple-200">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-14 h-14 flex items-center justify-center bg-purple-600 rounded-xl">
              <i className="ri-question-line text-3xl text-white"></i>
            </div>
            <h2 className="text-3xl font-bold text-purple-900">{t('elec_practice_title')}</h2>
          </div>

          <p className="text-lg text-gray-700 mb-6">{t('elec_practice_desc')}</p>

          <div className="space-y-6">
            {/* Question 1 */}
            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="text-lg font-semibold mb-4 text-gray-800">
                1. {t('elec_q1')}
              </h3>
              <div className="space-y-3">
                {['A', 'B', 'C'].map((option) => (
                  <button
                    key={option}
                    onClick={() => handleAnswerSelect(1, option)}
                    disabled={showResults}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      showResults ? 'cursor-default' : 'cursor-pointer'
                    } ${
                      selectedAnswers[1] === option
                        ? showResults
                          ? correctAnswers[1] === option
                            ? 'border-green-500 bg-green-50'
                            : 'border-red-500 bg-red-50'
                          : 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    <span className="font-semibold">{option}.</span> {t(`elec_q1_${option.toLowerCase()}`)}
                  </button>
                ))}
              </div>
            </div>

            {/* Question 2 */}
            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="text-lg font-semibold mb-4 text-gray-800">
                2. {t('elec_q2')}
              </h3>
              <div className="space-y-3">
                {['A', 'B', 'C'].map((option) => (
                  <button
                    key={option}
                    onClick={() => handleAnswerSelect(2, option)}
                    disabled={showResults}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      showResults ? 'cursor-default' : 'cursor-pointer'
                    } ${
                      selectedAnswers[2] === option
                        ? showResults
                          ? correctAnswers[2] === option
                            ? 'border-green-500 bg-green-50'
                            : 'border-red-500 bg-red-50'
                          : 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    <span className="font-semibold">{option}.</span> {t(`elec_q2_${option.toLowerCase()}`)}
                  </button>
                ))}
              </div>
            </div>

            {/* Question 3 */}
            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="text-lg font-semibold mb-4 text-gray-800">
                3. {t('elec_q3')}
              </h3>
              <div className="space-y-3">
                {['A', 'B', 'C'].map((option) => (
                  <button
                    key={option}
                    onClick={() => handleAnswerSelect(3, option)}
                    disabled={showResults}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      showResults ? 'cursor-default' : 'cursor-pointer'
                    } ${
                      selectedAnswers[3] === option
                        ? showResults
                          ? correctAnswers[3] === option
                            ? 'border-green-500 bg-green-50'
                            : 'border-red-500 bg-red-50'
                          : 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    <span className="font-semibold">{option}.</span> {t(`elec_q3_${option.toLowerCase()}`)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Results */}
          {showResults && (
            <div className="mt-6 bg-white p-6 rounded-xl shadow-md">
              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-purple-100 rounded-full mx-auto mb-4">
                  <span className="text-3xl font-bold text-purple-600">{getScore()}/3</span>
                </div>
                <h3 className="text-2xl font-bold mb-2 text-gray-800">
                  {getScore() === 3 ? t('elec_perfect') : getScore() >= 2 ? t('elec_good') : t('elec_try_again')}
                </h3>
                <p className="text-gray-600 mb-4">
                  {t('elec_score_text').replace('{score}', getScore().toString()).replace('{total}', '3')}
                </p>
                <button
                  onClick={resetQuiz}
                  className="bg-purple-600 text-white px-8 py-3 rounded-lg hover:bg-purple-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t('elec_retry')}
                </button>
              </div>
            </div>
          )}

          {!showResults && Object.keys(selectedAnswers).length === 3 && (
            <div className="mt-6 text-center">
              <button
                onClick={checkAnswers}
                className="bg-purple-600 text-white px-8 py-3 rounded-lg hover:bg-purple-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('elec_check')}
              </button>
            </div>
          )}
        </div>

        {/* 8. Samenvatting */}
        <div className="bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-2xl p-8 shadow-lg">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-14 h-14 flex items-center justify-center bg-blue-500 rounded-xl">
              <i className="ri-file-list-3-line text-3xl text-white"></i>
            </div>
            <h2 className="text-3xl font-bold">{t('elec_summary_title')}</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              t('elec_sum1'),
              t('elec_sum2'),
              t('elec_sum3'),
              t('elec_sum4'),
              t('elec_sum5'),
              t('elec_sum6'),
              t('elec_sum7'),
              t('elec_sum8')
            ].map((point, index) => (
              <div key={index} className="flex items-start space-x-3">
                <div className="w-8 h-8 flex items-center justify-center bg-blue-500 rounded-lg flex-shrink-0 mt-1">
                  <i className="ri-check-line text-xl text-white"></i>
                </div>
                <p className="text-blue-50 leading-relaxed">{point}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 bg-blue-500 p-6 rounded-xl">
            <p className="text-xl text-center font-semibold">
              {t('elec_final_message')}
            </p>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link 
            to="/seguranca"
            className="flex items-center text-blue-600 hover:text-blue-700 cursor-pointer"
          >
            <div className="w-5 h-5 flex items-center justify-center mr-2">
              <i className="ri-arrow-left-line"></i>
            </div>
            {t('nav_safety')}
          </Link>
          <Link 
            to="/quiz1"
            className="flex items-center bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            {t('nav_quiz1')}
            <div className="w-5 h-5 flex items-center justify-center ml-2">
              <i className="ri-question-line"></i>
            </div>
          </Link>
        </div>
      </div>
    </Layout>
  );
}
